import React, { useState } from 'react';
import { Alert, Button, Col, Collapse, DatePicker, Form, Input, Modal, Radio, Row, Select, message } from 'antd';
import dayjs from 'dayjs';
import PatientService from '../../../services/PatientService';
import { US_STATES, formatPhoneNumber } from '../../../components/BusDataFormWizard/constants';
import { RACE_OPTIONS } from '../../../constants/raceEthnicity';

const INSURANCE_OPTIONS = [
  { value: 'private', label: 'Private' },
  { value: 'insuredThroughEmployer', label: 'Insured through employer' },
  { value: 'medicaid', label: 'Medicaid' },
  { value: 'medicare', label: 'Medicare' },
  { value: 'military', label: 'Military' },
  { value: 'noCoverage', label: 'No coverage' }
];

/**
 * Members → Add member. Name, email and phone are all that's required; the
 * optional details can be filled in now or left for their first intake.
 */
const AddMemberModal = ({ open, onClose, onCreated }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [duplicate, setDuplicate] = useState(null);

  const close = () => {
    form.resetFields();
    setDuplicate(null);
    onClose();
  };

  const submit = async ({ openRecord = false, allowDuplicateEmail = false } = {}) => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      const res = await PatientService.createMember({
        ...values,
        dateOfBirth: values.dateOfBirth ? values.dateOfBirth.format('MM/DD/YYYY') : undefined,
        allowDuplicateEmail
      });
      if (res?.code === 'DUPLICATE_EMAIL') {
        setDuplicate(res.existing);
        return;
      }
      if (!res?.success) {
        if (res?.errors) {
          form.setFields(Object.entries(res.errors).map(([name, error]) => ({ name, errors: [error] })));
        }
        message.error(res?.error || 'Could not create the member');
        return;
      }
      message.success(`${res.data.name} added`);
      form.resetFields();
      setDuplicate(null);
      onCreated(res.data.patientId, { openRecord });
    } catch {
      message.error('Could not create the member');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Add member"
      onCancel={close}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={close}>Cancel</Button>,
        <Button key="open" loading={saving} onClick={() => submit({ openRecord: true })}>Create &amp; open record</Button>,
        <Button key="create" type="primary" loading={saving} onClick={() => submit()}>Create member</Button>
      ]}
    >
      <Form form={form} layout="vertical" requiredMark="optional" onValuesChange={() => duplicate && setDuplicate(null)}>
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item name="firstName" label="First name" rules={[{ required: true, whitespace: true, message: 'First name is required' }]}>
              <Input maxLength={100} autoFocus />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="lastName" label="Last name" rules={[{ required: true, whitespace: true, message: 'Last name is required' }]}>
              <Input maxLength={100} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="emailAddress" label="Email" rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}>
          <Input maxLength={254} />
        </Form.Item>
        <Form.Item
          name="phoneNumber"
          label="Phone"
          normalize={(v) => formatPhoneNumber(v || '')}
          rules={[{
            required: true,
            validator: (_, v) => (String(v || '').replace(/\D/g, '').length >= 10
              ? Promise.resolve()
              : Promise.reject(new Error('Enter a 10-digit phone number')))
          }]}
        >
          <Input placeholder="(XXX) XXX-XXXX" />
        </Form.Item>

        {duplicate && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={`${duplicate.name || 'A member'} already uses this email`}
            description={(
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <Button size="small" onClick={() => { close(); onCreated(duplicate.patientId, { openRecord: true, existing: true }); }}>
                  Open their record
                </Button>
                <Button size="small" onClick={() => submit({ allowDuplicateEmail: true })}>Create anyway</Button>
              </div>
            )}
          />
        )}

        <Collapse
          ghost
          items={[{
            key: 'details',
            label: 'Member details (optional)',
            forceRender: true,
            children: (
              <>
                <Row gutter={12}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="dateOfBirth" label="Date of birth">
                      <DatePicker
                        format="MM/DD/YYYY"
                        style={{ width: '100%' }}
                        disabledDate={(d) => d && d.isAfter(dayjs())}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="birthGender" label="Birth gender">
                      <Radio.Group options={[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }]} />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="raceEthnicity" label="Race/ethnicity">
                  <Select allowClear options={RACE_OPTIONS} />
                </Form.Item>
                <Form.Item name="insuranceCoverage" label="Insurance coverage">
                  <Select allowClear options={INSURANCE_OPTIONS} />
                </Form.Item>
                <Row gutter={12}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="stateOfResidence" label="State">
                      <Select allowClear showSearch optionFilterProp="label" options={US_STATES} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="eventZip" label="Zip code" rules={[{ pattern: /^\d{5}$/, message: 'Enter a 5-digit zip' }]}>
                      <Input maxLength={5} />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="testingCode" label="Kit testing code" rules={[{ pattern: /^[A-Za-z0-9-]+$/, message: 'Letters, numbers and dashes only' }]}>
                  <Input maxLength={50} />
                </Form.Item>
              </>
            )
          }]}
        />
      </Form>
    </Modal>
  );
};

export default AddMemberModal;
